import React, {
	useState, useMemo, useRef,
} from 'react';
import classNames from 'classnames';
import * as Comlink from 'comlink';
import SearchWorker from 'worker-loader!../../workers/search-style-packs';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import Popover from '../general-components/popover';
import WaitingIndicator from '../general-components/waiting-indicator';

import { useDispatch, useSelector } from 'react-redux';
import { setStylePackChannelFilter } from '../../stores/actions/user-interface';
import {GreenroomState as GS} from '../../stores/types/types';
import { STYLE_PACK_FILTER, StylePackData } from '../types/stylepacks';

interface Props {
	filters: string[];
	items: StylePackData[];
	onResult: (results: StylePackData[] | null) => void;
}

let searchWorker: any;

(async function init() {
	const worker = new SearchWorker();
	searchWorker = Comlink.wrap(worker) as any;
}());

export default function StylePacksMenu(props: Props) {
	const {
		items,
		filters,
		onResult,
	} = props;

	const dispatch = useDispatch();
	const selectedFilter = useSelector((s: GS) => s.UserInterfaceReducer.stylepackChannelFilter);
	const stylePackTypeFilter = useSelector((s: GS) => s.UserInterfaceReducer.stylePackTypeFilter);

	const [searchString, setSearchString] = useState<string>('');
	const [filterDropdownOpen, setFilterDropdownOpen] = useState<boolean>(false);
	const [filterLoading, setFilterLoading] = useState<boolean>(false);
	const [result, setResult] = useState<StylePackData[] | null>(null);

	const searchDebounce = useRef<NodeJS.Timeout | null>(null);

	const finalize = (results: StylePackData[] | null) => {
		setFilterLoading(false);
		setResult(results);
		onResult(results);
	}

	const filterMedia = (
		inputSearchString: string,
		inputFilterType: string,
	) => {
		searchWorker
			.search(
				inputSearchString,
				items,
				inputFilterType,
			)
			.then(async () => {
				const results = await searchWorker.results;
				finalize(results);
			});
	};

	const handleFilterMedia = ({ target }: React.KeyboardEvent<HTMLInputElement>) => {
		if (!(target instanceof HTMLInputElement)) return;

		if (searchDebounce.current) clearTimeout(searchDebounce.current);
		if (target.value) {
			searchDebounce.current = setTimeout(() => {
				filterMedia(target.value, selectedFilter ?? STYLE_PACK_FILTER.ALL);
			}, 250);
		} else {
			finalize(null);
		}
	};

	const handleClearFilters = () => {
		setSearchString('');
		dispatch(setStylePackChannelFilter(STYLE_PACK_FILTER.ALL));
		finalize(null);
	};

	const resultAmount = useMemo(() => {
		const resultWithTypeFilter = stylePackTypeFilter 
			? result?.filter((result) => result.stylePack.some((style) => style.type === stylePackTypeFilter)) 
			: result;
		return resultWithTypeFilter?.length ?? 0;
	}, [stylePackTypeFilter, result]);

	return (
		<>
			<div className={classNames('search-and-upload-container position-relative stylepacks')}>
				<div className={classNames('search-field-media no-upload', {filtered: searchString})}>
					<input
						type="text"
						value={searchString}
						className='bl-font-p2'
						onKeyUp={handleFilterMedia}
						placeholder={`Search items`}
						onChange={(e) => {
							setFilterLoading(true);
							setSearchString(e.target.value);
						}}
					/>
					<span className="search-icon">
						<Icon name={ICONS.SEARCH} color={COLORS.DEFAULT_GRAY} size={14} />
					</span>
				</div>
			
				<div 
					className={classNames("filter-container stylepacks", {squish: searchString})} 
					onClick={() => setFilterDropdownOpen(true)}
				>
					<div className={classNames('filter-label bl-font-p2', {
						isopen: filterDropdownOpen,
						isapplied: selectedFilter !== STYLE_PACK_FILTER.ALL,
						
					})}>
						<span>{selectedFilter}</span>
						<Icon name={ICONS.KEYBOARD_ARROW_DOWN} color={COLORS.WHITE} size={10}/>
					</div>
					<Popover open={filterDropdownOpen} close={() => setFilterDropdownOpen(false)}>
						<div className='filter-options bl-font-p2'>
							<div className='filter-option-header'><span className='bl-font-c1'>SHOW</span></div>
							{filters.map((SPFilter: string, index: number) => (
								<div
									key={index}
									className={classNames('filter-option', SPFilter === selectedFilter && 'selected file-type')}
									onMouseUp={() => {
										setFilterLoading(true);
										dispatch(setStylePackChannelFilter(SPFilter));
										setFilterDropdownOpen(false);
										filterMedia(searchString, SPFilter);
									}}
								>
									{SPFilter === selectedFilter && (
										<Icon size={12} color={COLORS.WHITE} name={ICONS.CHECK} className="margin-right-small" />
									)}
									<span>
										{SPFilter}
									</span>
								</div>
							))}
						</div>
					</Popover>
				</div>
			</div>
			<div className={classNames('media-filters-results-container stylepacks', {
					'show-loading-results': searchString || selectedFilter !== STYLE_PACK_FILTER.ALL,
					'hide-loading-results': !searchString && selectedFilter === STYLE_PACK_FILTER.ALL,
					'show-results-string': !filterLoading,
				})}>
					<div className='stp-loading-indicator'><WaitingIndicator /></div> 
					<h6 className='bl-font-header margin-none stp-results-indicator'>
						{`${resultAmount} items found`}
						<button onClick={handleClearFilters} className='transparent primary'>
							<Icon className='margin-right-small' name={ICONS.CLOSE} color={COLORS.PALE_GREEN} size={10} />
							<span className='text-green bl-font-s3 white-space-nowrap'>Clear</span>
						</button>
					</h6>
			</div>
		</>
	);
}
